import { API_ENDPOINTS, httpClientInstance } from "@/api"
import type { CreatePerformancePolicyRequest, PerformancePolicy } from "@/types"
import { create } from "zustand"

/** An API failure with the API's own message (first field error, else the message). */
const toMessage = (error: unknown, fallback: string): string => {
  const body = (error as { response?: { data?: { message?: string; detail?: string; errors?: Record<string, string[]> } } })
    ?.response?.data
  const firstFieldMessage = body?.errors ? Object.values(body.errors)[0]?.[0] : undefined
  return firstFieldMessage ?? body?.message ?? body?.detail ?? (error instanceof Error ? error.message : fallback)
}

interface PerformancePolicyStore {
  /** Stores a monitoring policy for a server. Throws an Error with the API's message on failure. */
  createPolicy: (payload: CreatePerformancePolicyRequest) => Promise<PerformancePolicy>
}

/**
 * Per-server performance monitoring policies (/v1/performance-alerts/). Policies are stored only;
 * nothing evaluates them yet. The Performance Alert page still uses its own mocked store.
 */
export const usePerformancePolicyStore = create<PerformancePolicyStore>(() => ({
  createPolicy: async (payload) => {
    try {
      const response = await httpClientInstance.post<{ data: PerformancePolicy }>(
        API_ENDPOINTS.performanceAlerts.root,
        payload
      )
      return response.data
    } catch (error) {
      throw new Error(toMessage(error, "Failed to save the performance monitoring policy"))
    }
  },
}))
